// Vendor Libs
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
  CircularProgress,
  Grid,
  InputAdornment,
  makeStyles,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import SettingsIcon from '@material-ui/icons/Settings';

// Dish Tech React
import {CheckAuthSession} from '@dishtech-react/core';

// Redux
import {useDispatch, useSelector} from 'react-redux';
import SearchIcon from '@material-ui/icons/Search';
import {getData} from '../redux/actions/actions';

// Components
import Layout from './Layout';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  main: {
    marginTop: 90,
  },
  loading: {marginLeft: 20, color: theme.palette.secondary.main},
  childGrid: {padding: 25, width: 450, wordWrap: 'break-word'},
  buttonPortal: {
    padding: 45,
    borderRadius: 17,
    fontSize: 16,
    fontWeight: 400,
    cursor: 'pointer',
    filter: 'brightness(0.94)',
    border: '1px solid #DDD8D7',
    '&:hover': {
      color: 'white',
      background: theme.palette.primary.main,
      borderRadius: 17,
      fontWeight: 800,
      outline: 'none',
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 15px ${theme.palette.primary.main}`,
    },
  },
  customTooltip: {
    backgroundColor: theme.palette.secondary.main,
    fontSize: 14,
    fontWeight: 700,
  },
  customArrow: {
    color: theme.palette.secondary.main,
  },
  gear: {
    float: 'right',
    color: theme.palette.secondary.main,
    fontSize: 50,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  error: {
    color: 'white',
  },
  search: {
    color: theme.palette.secondary.main,
    fontSize: 30,
  },
  errorPaper: {
    padding: 10,
    backgroundColor: '#e00842',
  },
}));

const Raindrop = ({inputName, inputEmail}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const dataList = useSelector((state) => state?.usersList);
  const {loading, error} = dataList;
  const [clients, setClients] = useState([]);
  const [backError, setBackError] = useState(null);
  const [origClients, setOrigClients] = useState([]);

  useEffect(() => {
    if (inputEmail) {
      dispatch(getData(inputEmail));
    }
  }, [dispatch, inputEmail]);

  useEffect(() => {
    if (dataList?.users?.error) {
      setBackError(dataList?.users?.error);
    } else {
      setClients(dataList?.users?.clients);
      setOrigClients(dataList?.users?.clients);
      setBackError(null);
    }
  }, [dataList]);

  const openTab = (url) => {
    if (url && url.length) {
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('target', '_blank');
      a.setAttribute('features', 'noopener,noreferrer');
      a.click();
    }
  };

  return (
    <Layout email={inputEmail} userName={inputName}>
      <CheckAuthSession />
      <div className={classes.main}>
        {loading ? (
          <div className={classes.root}>
            <CircularProgress />
            <Typography className={classes.loading} variant="h5">
              Loading...
            </Typography>
          </div>
        ) : error || backError ? (
          <div className={classes.root}>
            <Grid align="center" className={classes.root} container={true} justifyContent="center">
              <Paper className={classes.errorPaper}>
                <Typography className={classes.error} variant="h4">
                  Error: {error || backError}
                </Typography>
              </Paper>
            </Grid>
          </div>
        ) : (
          <div>
            <div className={classes.root}>
              <Grid align="center" className={classes.root} container={true} justifyContent="center" spacing={3}>
                <Grid item={true} xs={3} />
                <Grid item={true} xs={3}>
                  <TextField
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon className={classes.search} />
                        </InputAdornment>
                      ),
                    }}
                    id="standard-basic"
                    onChange={(event) => {
                      setClients(
                        origClients.filter((o) => o.name.toLowerCase().includes(event.target.value.toLowerCase()))
                      );
                    }}
                  />
                </Grid>
                <Grid item={true} xs={3}>
                  <Tooltip
                    arrow={true}
                    classes={{
                      tooltip: classes.customTooltip,
                      arrow: classes.customArrow,
                    }}
                    title="Click to go to the Ripple"
                  >
                    <SettingsIcon className={classes.gear} onClick={() => openTab(Config.oauth.rippleUrl)} />
                  </Tooltip>
                </Grid>
              </Grid>
            </div>
            <Grid align="center" className={classes.root} container={true} justifyContent="center">
              {clients?.map((app, index) => {
                const {name, url, roles} = app;
                if (!url.length) {
                  return null;
                }
                return (
                  <Grid className={classes.childGrid} item={true} key={index}>
                    <Tooltip
                      arrow={true}
                      classes={{
                        tooltip: classes.customTooltip,
                        arrow: classes.customArrow,
                      }}
                      key={index}
                      title={
                        <div>
                          Roles:
                          {roles.map((r, indexU) => (
                            <div key={indexU}>{r}</div>
                          ))}
                        </div>
                      }
                    >
                      <Paper className={classes.buttonPortal} elevation={4} onClick={() => openTab(url)}>
                        <Grid align="center" item={true}>
                          <span>{name.toUpperCase()}</span>
                        </Grid>
                      </Paper>
                    </Tooltip>{' '}
                  </Grid>
                );
              })}
            </Grid>
          </div>
        )}
      </div>
    </Layout>
  );
};

Raindrop.propTypes = {
  inputEmail: PropTypes.string,
  inputName: PropTypes.string,
};

export default Raindrop;
